import { Component } from "react"

/* Components */
import { Input } from "../../components"

/* Modal */
import Modal from "react-modal"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { variables, get, create, edit } from "../../api/Manager"

/* Helpers */
import { utils } from "../../helpers"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"
import { CloseIcon, CheckIcon, InfoIcon, ROLE_ICONS } from "./ManagerIcons"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* EMPTY FORM STATE */
const EMPTY = {
    id: 0,
    type: "create",
    status: "loading",
    roles: [],
    studios: [],
    operators: [],
    name: "",
    email: "",
    password: "",
    access: "",
    studioIDs: [],
    operatorIDs: [],
    waiting: false,
}


/* Widget Manager Form */
class ManagerForm extends Component {

    constructor() {
        super()
        this.state = { visible: false, ...EMPTY }
    }

    /* Open Action */
    open = ({ type = "create", id = 0 }) => {

        this.setState({ visible: true, ...EMPTY, type, id: parseInt(id) ? parseInt(id) : 0 }, () => {
            this.load()
        })
    }

    /* Close Action */
    close = () => {
        this.setState({ visible: false, ...EMPTY })
    }

    /* Load Action — create reads the variables, edit reads the admin together with them */
    load = () => {

        /* Fields */
        const { type, id } = this.state

        /* Set Status */
        this.setState({ status: "loading" })

        /* Edit */
        if (type === "edit") {

            get({ id }).then((response) => {
                if (response.status === 200) {
                    this.setState({
                        status: "ok",
                        roles: response.data.roles,
                        studios: response.data.studios,
                        operators: response.data.operators,
                        name: utils.checkValue(response.data.admin.name, ""),
                        email: utils.checkValue(response.data.admin.email, ""),
                        access: utils.checkValue(response.data.admin.access, ""),
                        studioIDs: response.data.studioIDs,
                        operatorIDs: response.data.operatorIDs,
                    })
                }
                else {
                    this.setState({ status: "error" })
                    Swal.fire(utils.notification(this.message(response), "error"))
                    this.close()
                }
            }).catch(() => {
                this.setState({ status: "error" })
                Swal.fire(utils.notification(t("swal.generic"), "error"))
                this.close()
            })

            return
        }

        /* Create */
        variables().then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", roles: response.data.roles, studios: response.data.studios, operators: response.data.operators })
            }
            else {
                this.setState({ status: "error" })
                Swal.fire(utils.notification(this.message(response), "error"))
                this.close()
            }
        }).catch(() => {
            this.setState({ status: "error" })
            Swal.fire(utils.notification(t("swal.generic"), "error"))
            this.close()
        })

    }

    /* Read The Error Message Of A Response */
    message = (response) => {

        if (response && response.data && response.data.message) {
            return response.data.message
        }

        if (response && response.status === 498) {
            return t("swal.invalidKey")
        }

        if (response && (response.status === 401 || response.status === 403)) {
            return t("swal.noAccess")
        }

        return t("swal.generic")
    }

    /* Change Role — the scope of the previous role is dropped */
    changeAccess = (access) => {
        this.setState({ access, studioIDs: [], operatorIDs: [] })
    }

    /* Toggle Studio — a pitboss keeps a single studio, a viewer keeps many */
    toggleStudio = (id) => {

        /* Fields */
        const { access, studioIDs } = this.state
        const studioID = parseInt(id)

        /* Pitboss */
        if (access === "pitboss") {
            this.setState({ studioIDs: studioIDs.includes(studioID) ? [] : [studioID] })
            return
        }

        /* Viewer */
        if (studioIDs.includes(studioID)) {
            this.setState({ studioIDs: studioIDs.filter((item) => parseInt(item) !== studioID) })
        }
        else {
            this.setState({ studioIDs: [...studioIDs, studioID] })
        }
    }

    /* Toggle Operator */
    toggleOperator = (id) => {

        /* Fields */
        const { operatorIDs } = this.state
        const operatorID = parseInt(id)

        /* Check Selection */
        if (operatorIDs.includes(operatorID)) {
            this.setState({ operatorIDs: operatorIDs.filter((item) => parseInt(item) !== operatorID) })
        }
        else {
            this.setState({ operatorIDs: [...operatorIDs, operatorID] })
        }
    }

    /* Save Action */
    save = () => {

        /* Fields */
        const { type, id, name, email, password, access, studioIDs, operatorIDs } = this.state
        const { reload } = this.props

        /* Check Name */
        if (!name) {
            this._name.error(t("manager.error.emptyName"))
            return
        }

        /* Check Email */
        if (!email) {
            this._email.error(t("manager.error.emptyEmail"))
            return
        }

        /* Check Email Format */
        if (/\S+@\S+\.\S+/.test(email) === false) {
            this._email.error(t("manager.error.invalidEmail"))
            return
        }

        /* Check Password */
        if (type === "create") {

            if (!password) {
                this._password.error(t("manager.error.emptyPassword"))
                return
            }

            if (password.length < 8) {
                this._password.error(t("manager.error.shortPassword"))
                return
            }
        }

        /* Check Role */
        if (!access) {
            Swal.fire(utils.notification(t("manager.error.selectRole"), "error"))
            return
        }

        /* Check Pitboss Scope */
        if (access === "pitboss" && studioIDs.length !== 1) {
            Swal.fire(utils.notification(t("manager.error.oneStudio"), "error"))
            return
        }

        /* Check Viewer Scope */
        if (access === "viewer" && studioIDs.length === 0) {
            Swal.fire(utils.notification(t("manager.error.emptyStudios"), "error"))
            return
        }

        if (access === "viewer" && operatorIDs.length === 0) {
            Swal.fire(utils.notification(t("manager.error.emptyOperators"), "error"))
            return
        }

        /* Start Waiting */
        this.setState({ waiting: true })

        /* Prepare Request */
        const request = type === "edit"
            ? edit({ id, name, email, access, studioIDs, operatorIDs })
            : create({ name, email, password, access, studioIDs, operatorIDs })

        /* Send to REST API */
        request.then((response) => {
            if (response.status === 200) {
                Swal.fire(utils.notification(type === "edit" ? t("manager.savedSuccess") : t("manager.addedSuccess"), "success"))
                reload()
                this.close()
            }
            else {
                Swal.fire(utils.notification(this.message(response), "error"))
            }
        }).catch(() => {
            Swal.fire(utils.notification(t("swal.generic"), "error"))
        }).finally(() => {
            this.setState({ waiting: false })
        })

    }

    /* Draw Roles */
    _roles = () => {

        /* Fields */
        const { roles, access } = this.state

        return (
            <div className="manager--section">

                <div className="manager--section-head">
                    <div className="manager--section-title">{t("manager.role")}</div>
                </div>

                <div className="manager--roles">
                    {roles.map((item, index) => {

                        /* Role Icon */
                        const RoleIcon = ROLE_ICONS[item.slug] ? ROLE_ICONS[item.slug] : ROLE_ICONS.manager

                        return (
                            <div onClick={() => this.changeAccess(item.slug)} className={`manager--role ${item.slug} ${access === item.slug ? "active" : ""}`} key={`${index}`}>

                                <div className="manager--role-check"><CheckIcon /></div>
                                <div className="manager--role-icon"><RoleIcon /></div>
                                <div className="manager--role-name">{item.name}</div>
                                <div className="manager--role-text">{t(`manager.roleText.${item.slug}`)}</div>

                            </div>
                        )
                    })}
                </div>

            </div>
        )
    }

    /* Draw Chips */
    _chips = (list, selected, onSelect, single = false, green = false) => {

        /* Check List */
        if (list.length === 0) {
            return <div className="manager--chips-empty">{t("manager.nothingAvailable")}</div>
        }

        return (
            <div className="manager--chips">
                {list.map((item, index) =>
                    <div
                        onClick={() => onSelect(item.id)}
                        className={`manager--chip ${green ? "green" : ""} ${single ? "round" : ""} ${selected.includes(parseInt(item.id)) ? "active" : ""}`}
                        key={`${index}`}
                    >
                        <div className="manager--chip-mark"><CheckIcon size={11} /></div>
                        <div className="manager--chip-text">
                            <div className="manager--chip-name">{item.name}</div>
                            {item.slug ? <div className="manager--chip-slug">{item.slug}</div> : null}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    /* Draw Scope — a pitboss picks one studio, a viewer picks studios and operators */
    _scope = () => {

        /* Fields */
        const { access, studios, operators, studioIDs, operatorIDs } = this.state

        /* Manager */
        if (access === "manager") {
            return (
                <div className="manager--section">
                    <div className="manager--hint">
                        <InfoIcon />
                        <span>{t("manager.scopeGlobalHint")}</span>
                    </div>
                </div>
            )
        }

        /* Pitboss */
        if (access === "pitboss") {
            return (
                <div className="manager--section">

                    <div className="manager--section-head">
                        <div className="manager--section-title">
                            {t("manager.selectStudio")}
                            <span className={`manager--counter ${studioIDs.length === 0 ? "empty" : ""}`}>{studioIDs.length} / 1</span>
                        </div>
                    </div>

                    {this._chips(studios, studioIDs, this.toggleStudio, true, true)}

                    <div className="manager--section">
                        <div className="manager--hint">
                            <InfoIcon />
                            <span>{t("manager.pitbossHint")}</span>
                        </div>
                    </div>

                </div>
            )
        }

        /* Viewer */
        if (access === "viewer") {
            return (
                <>
                    <div className="manager--section">

                        <div className="manager--section-head">
                            <div className="manager--section-title">
                                {t("manager.studios")}
                                <span className={`manager--counter ${studioIDs.length === 0 ? "empty" : ""}`}>{studioIDs.length}</span>
                            </div>
                            <div onClick={() => this.setState({ studioIDs: studioIDs.length === studios.length ? [] : studios.map((item) => parseInt(item.id)) })} className="manager--link">
                                {studioIDs.length === studios.length ? t("common.clearAll") : t("common.selectAll")}
                            </div>
                        </div>

                        {this._chips(studios, studioIDs, this.toggleStudio, false, true)}

                    </div>

                    <div className="manager--section">

                        <div className="manager--section-head">
                            <div className="manager--section-title">
                                {t("manager.operators")}
                                <span className={`manager--counter ${operatorIDs.length === 0 ? "empty" : ""}`}>{operatorIDs.length}</span>
                            </div>
                            <div onClick={() => this.setState({ operatorIDs: operatorIDs.length === operators.length ? [] : operators.map((item) => parseInt(item.id)) })} className="manager--link">
                                {operatorIDs.length === operators.length ? t("common.clearAll") : t("common.selectAll")}
                            </div>
                        </div>

                        {this._chips(operators, operatorIDs, this.toggleOperator)}

                    </div>

                    <div className="manager--section">
                        <div className="manager--hint">
                            <InfoIcon />
                            <span>{t("manager.viewerHint")}</span>
                        </div>
                    </div>
                </>
            )
        }

        /* Role Is Not Selected Yet */
        return (
            <div className="manager--section">
                <div className="manager--hint">
                    <InfoIcon />
                    <span>{t("manager.selectRoleHint")}</span>
                </div>
            </div>
        )
    }

    /* Draw Body */
    _body = () => {

        /* Fields */
        const { status, type, name, email, password } = this.state

        /* Check Status */
        if (status !== "ok") {
            return <div className="manager--modal-loading"><LoadingOutlined /></div>
        }

        return (
            <div className="manager--modal-body">

                <Input ref={(ref) => this._name = ref} label={t("manager.name")} value={name} onChange={(event) => this.setState({ name: event.target.value })} />
                <Input ref={(ref) => this._email = ref} label="Email" value={email} onChange={(event) => this.setState({ email: event.target.value })} />

                {type === "create" &&
                    <>
                        <Input ref={(ref) => this._password = ref} label={t("auth.password")} value={password} onChange={(event) => this.setState({ password: event.target.value })} secure />
                        <div onClick={() => this.setState({ password: utils.generatePassword() })} className="manager--generate">{t("common.generatePassword")}</div>
                    </>
                }

                {/* Draw Roles */}
                {this._roles()}

                {/* Draw Scope */}
                {this._scope()}

            </div>
        )
    }

    /* Draw Footer */
    _footer = () => {

        /* Fields */
        const { status, waiting } = this.state

        /* Check Status */
        if (status !== "ok") {
            return null
        }

        return (
            <div className="manager--modal-footer">

                <div onClick={() => this.close()} className="manager--button ghost">{t("common.cancel")}</div>

                {waiting
                    ? <div className="manager--button primary"><LoadingOutlined /></div>
                    : <div onClick={() => this.save()} className="manager--button primary">{t("common.save")}</div>
                }

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, type } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="manager--modal"
                overlayClassName="manager--overlay"
                closeTimeoutMS={200}
            >

                <div className="manager--modal-header">

                    <div>
                        <h2>{type === "edit" ? t("manager.editTitle") : t("manager.createTitle")}</h2>
                        <p>{type === "edit" ? t("manager.editSubtitle") : t("manager.createSubtitle")}</p>
                    </div>

                    <div onClick={() => this.close()} className="manager--modal-close">
                        <CloseIcon />
                    </div>

                </div>

                {/* Draw Body */}
                {this._body()}

                {/* Draw Footer */}
                {this._footer()}

            </Modal>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(ManagerForm)
